
import { AsyncTask, AsyncTaskStatus, AsyncTaskType } from '@/types/catalogs/AsyncTask'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import Vue from 'vue'
import FillgridService from '@/services/FillgridService'
import { CustomSearch, SearchDateType, SearchOrigin } from '@/types/catalogs/CustomSearch'
import { CustomView } from '@/types/catalogs/Views'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import LogService from '@/services/support/LogService'

export default Vue.extend({
  components: {
    LoadingMask
  },
  props: {
    taskId: {
      type: Number,
      required: true
    },
    permissionToCancel: {
      type: Boolean,
      default: false
    },
    permissionToRefresh: {
      type: Boolean,
      default: false
    },
    permissionToReadSearches: {
      type: Boolean,
      default: false
    },
    permissionToReadViews: {
      type: Boolean,
      default: false
    },
    loadedSearches: {
      type: Object,
      default: () => {}
    },
    loadedViews: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      loading: false,
      task: new AsyncTask(),
      taskStati: AsyncTaskStatus,
      taskTypes: AsyncTaskType,
      search: {} as CustomSearch,
      showSearch: false,
      view: {} as CustomView,
      showView: false
    }
  },
  mounted () {
    this.loadTask()
  },
  updated () {
    this.loading = false
  },
  watch: {
    taskId (): void {
      this.loadTask()
    }
  },
  computed: {
    /**
     * Try to get the translation for the task error details
     * @returns the error translated or the original error
     */
    getErrorDetail (): string {
      if (this.task.process && this.task.process.error) {
        const error = this.task.process.error
        return error.description
      }
      return ''
    },
    /**
     * @returns true if the task could be canceled
     */
    availableToCancel (): boolean {
      const stati = [this.taskStati.NEW, this.taskStati.PROCESSING, this.taskStati.WAITING]
      return !stati.includes(this.task.status)
    },
    taskPercentageString (): string {
      if (this.task.percentage) {
        if (this.task.percentage.toString().includes('.')) return this.task.percentage.toFixed(2)
        else return this.task.percentage.toFixed(0)
      }
      return '0'
    },
    taskAttachmentPercentageString (): string {
      if (this.task.attachmentPercentage) {
        if (this.task.attachmentPercentage.toString().includes('.')) return this.task.attachmentPercentage.toFixed(2)
        else return this.task.attachmentPercentage.toFixed(0)
      }
      return '0'
    },
    isDatafeedTask (): boolean {
      return this.task.type === AsyncTaskType.DATAFEED
    },
    isIntegrationTask (): boolean {
      return this.task.type === AsyncTaskType.INTEGRATION
    },
    availableDownloadLinkForDatafeed (): boolean {
      return this.task.availableResultsLink()
    },
    validateHasAttachments (): boolean {
      return this.task.process !== undefined &&
        this.task.process.attachmentIds !== undefined &&
        this.task.process.attachmentIds.length > 0
    },
    validateTaskInProcess (): boolean {
      return this.task.status === AsyncTaskStatus.PROCESSING
    },
    downloadFormatTask (): string {
      return this.validateHasAttachments
        ? '.ZIP'
        : '.CSV'
    }
  },
  methods: {
    /**
     * Transform the string format of the task
     * from dd/mm/yy, hh:mm to yyyy-mm-ddThh:mm
     *
     * @param date with format dd/mm/yy, hh:mm
     * @return a string with the same date but in ISO string format
     */
    stringForDate (date: string): string {
      let dateAndTime = date.split(',').map((part: string) => part.trim()) // [dd/mm/yy, hh:mm]
      dateAndTime[0] = dateAndTime[0].split('/').reverse().join('-') // [yy-mm-dd, hh:mm]
      let fullDate = dateAndTime[0].split('-') // [yy, mm, dd]
      fullDate[0] = fullDate[0].padStart(4, '20') // [20yy, mm, dd]
      dateAndTime[0] = fullDate.join('-') // [yyyy-mm-dd, hh:mm]
      return dateAndTime.join('T')
    },
    closeForm (): void {
      this.$emit('close')
    },
    refreshTask (): void {
      this.loadTask()
      this.$emit('updateAsyncTask', this.task)
    },
    cancelTask (): void {
      this.$emit('cancel')
    },
    /**
     * Get the information of the search related to the asynctask
     */
    async getTask (): Promise<any> {
      await CatalogsFillsService.getAsyncTaskById(this.taskId)
        .then((task) => {
          this.task = AsyncTask.createInstance(task)
        })
        .catch((error) => {
          LogService.log(`Error al obtener la información de la búsqueda: ${error}`)
        })
    },
    /**
     * Load the current task info
     */
    loadTask (): void {
      this.loading = true
      this.getTask()
        .then(() => {
          this.loadSearchDetails()
        })
    },
    /**
     * Get the information of the search related to the asynctask
     */
    async getSearchDetail (): Promise<any> {
      this.search = {} as CustomSearch
      this.view = {} as CustomView
      if (this.task.process.search) {
        let localSearch = this.loadedSearches[this.task.process.search]
        if (localSearch) {
          this.search = localSearch
        } else {
          await FillgridService.getSearch(this.task.template.id, this.task.process.search)
            .then((search) => {
              this.search = search
              this.$emit('save-search', search)
            })
            .catch((error) => {
              LogService.log(`Error al obtener la información de la búsqueda: ${error}`)
            })
        }
      }
    },
    /**
     * Get the information of the view related to the search
     */
    async getViewDetail (): Promise<any> {
      if (this.search.view) {
        let localView = this.loadedViews[this.search.view]
        if (localView) {
          this.view = localView
          return Promise.resolve(0)
        } else {
          await FillgridService.getViewById(this.task.template.id, this.search.view)
            .then((view) => {
              this.view = view
              this.$emit('save-view', view)
            })
            .catch((error) => {
              LogService.log(`Error al obtener la información de la vista de la búsqueda: ${error}`)
            })
        }
      }
    },
    /**
     * If the usar has the required permissions to read searches, it loads the search info
     * also if has permissions to read views, it loads the view info
     */
    loadSearchDetails (): void {
      if (this.permissionToReadSearches) {
        this.getSearchDetail()
          .then(() => {
            if (this.permissionToReadViews) this.getViewDetail()
          })
      }
    },
    /**
     * @param search to check its type
     * @returns the type of a search
     */
    searchType (search: CustomSearch): string {
      return search.origin === SearchOrigin.DATAFEED
        ? 'Datafeed'
        : 'Fillgrid'
    },
    /**
     * @returns a local date string based on a date string
     */
    toLocalDateString (date: string): string {
      return new Date(date).toLocaleDateString('es-MX')
    },
    /**
     * @param type of the task date
     * @returns the translation of the type of the search date
     */
    typeOfDate (type: SearchDateType): string {
      return type === SearchDateType.CREATION
        ? 'Fecha de creación'
        : 'Fecha de actualización'
    },
    async redirectUrlResult () {
      await CatalogsFillsService.getResultByID(this.task.id)
        .then(response => {
          const url:string = response.url
          window.location.href = url
        }).catch(() => {
          this.$bvToast.toast('Lo sentimos, ocurrió un error al descargar los resultados.', {
            title: 'Error de descarga',
            variant: 'danger',
            toaster: 'b-toaster-top-right',
            autoHideDelay: 4500
          })
        })
    }
  }
})
